import {
  isTaskPriority,
  isTaskRepeat,
  isTaskStatus,
  type TaskEmployeeLike,
  type TaskLike,
  type TaskPriority,
  type TaskProgress,
  type TaskRepeat,
} from '@agrogruda/shared';
import type { Json } from '@agrogruda/db/types';
import { supabase, type Row } from '@/lib/supabase';

export type TaskHistoryRow = Row<'task_progress_history'>;
export interface TaskRow extends Row<'tasks'> {
  task_progress_history: TaskHistoryRow[];
}

export const TASKS_KEY = ['tasks', 'list'] as const;
export const TASK_EMPLOYEES_KEY = ['tasks', 'employees'] as const;

/** PostgREST `max_rows` (packages/db/supabase/config.toml): lists are read in pages of this size. */
export const PAGE_SIZE = 1000;

function fail(message: string): never {
  throw new Error(message);
}

type PageResult<T> = PromiseLike<{ data: T[] | null; error: { message: string } | null }>;

async function fetchAll<T>(page: (from: number, to: number) => PageResult<T>): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await page(from, from + PAGE_SIZE - 1);
    if (error) fail(error.message);
    const rows = data ?? [];
    out.push(...rows);
    if (rows.length < PAGE_SIZE) break;
  }
  return out;
}

export function fetchTasks(): Promise<TaskRow[]> {
  return fetchAll((from, to) =>
    supabase
      .from('tasks')
      .select('*, task_progress_history(*)')
      .order('created_at', { ascending: false })
      .order('id')
      .range(from, to),
  );
}

export function fetchTaskEmployees(): Promise<TaskEmployeeLike[]> {
  return fetchAll((from, to) =>
    supabase.from('employees').select('id, name').order('name').order('id').range(from, to),
  );
}

/** DB row (`due_time` is `HH:MM:SS`) → the prototype-shaped TaskLike the shared helpers work on. */
export function toTaskLike(row: TaskRow): TaskLike {
  const history = [...row.task_progress_history]
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
    .map((h) => ({ progress: h.progress, at: h.created_at }));
  return {
    id: row.id,
    groupId: row.group_id,
    title: row.title,
    description: row.description,
    due: row.due_date,
    dueTime: (row.due_time ?? '').slice(0, 5),
    priority: isTaskPriority(row.priority) ? row.priority : 'normal',
    repeat: isTaskRepeat(row.repeat_rule) ? row.repeat_rule : 'none',
    assigneeId: row.assignee_employee_id,
    status: isTaskStatus(row.status) ? row.status : 'pending',
    progress: row.progress,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    previousTaskId: row.previous_task_id,
    history,
  };
}

export interface SaveTaskGroupInput {
  id?: string | null;
  title: string;
  description: string;
  dueDate: string;
  dueTime: string;
  priority: TaskPriority;
  repeat: TaskRepeat;
  assigneeIds: string[];
}

export interface SaveTaskGroupResult {
  groupId: string;
  taskIds: string[];
  created: number;
  toast: string;
}

function asRecord(data: Json | null, fn: string): Record<string, Json | undefined> {
  if (!data || typeof data !== 'object' || Array.isArray(data)) fail(fn);
  return data;
}

function jsonString(v: Json | undefined): string {
  return typeof v === 'string' ? v : '';
}

/** RPC save_task_group (prototype saveTask). Errors carry the prototype toast text in `message`. */
export async function saveTaskGroupRpc(input: SaveTaskGroupInput): Promise<SaveTaskGroupResult> {
  const payload: Json = {
    id: input.id ?? null,
    title: input.title,
    description: input.description,
    dueDate: input.dueDate,
    dueTime: input.dueTime,
    priority: input.priority,
    repeat: input.repeat,
    assigneeIds: input.assigneeIds,
  };
  const { data, error } = await supabase.rpc('save_task_group', { p: payload });
  if (error) fail(error.message);
  const r = asRecord(data, 'save_task_group');
  const ids = Array.isArray(r.taskIds) ? r.taskIds : [];
  return {
    groupId: jsonString(r.groupId),
    taskIds: ids.filter((x): x is string => typeof x === 'string'),
    created: Number(r.created ?? 0),
    toast: jsonString(r.toast),
  };
}

export interface SetTaskProgressResult {
  taskId: string;
  progress: number;
  status: string;
  toast: string;
  spawnedTaskId: string | null;
}

/** RPC set_task_progress (prototype setTaskProgress). */
export async function setTaskProgressRpc(
  taskId: string,
  progress: TaskProgress,
): Promise<SetTaskProgressResult> {
  const { data, error } = await supabase.rpc('set_task_progress', {
    p_task_id: taskId,
    p_progress: progress,
  });
  if (error) fail(error.message);
  const r = asRecord(data, 'set_task_progress');
  return {
    taskId: jsonString(r.taskId),
    progress: Number(r.progress ?? progress),
    status: jsonString(r.status),
    toast: jsonString(r.toast),
    spawnedTaskId: typeof r.spawnedTaskId === 'string' ? r.spawnedTaskId : null,
  };
}

/** RPC delete_task (prototype deleteTask). */
export async function deleteTaskRpc(taskId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_task', { p_task_id: taskId });
  if (error) fail(error.message);
}
